import { useTranslation } from 'react-i18next';
import { Card, Flex, Icon, Spinner, Text, cn } from '@kognitos/lattice';

import { formatBytes } from '@/shared/utils';

import { useAgentArtifactViewer } from './AgentArtifactViewer';
import {
  getArtifactFileIcon,
  getArtifactTypeLabel,
  type IAgentArtifactFile,
} from '../utils/artifact-file';

/**
 * What the write is doing, which decides how the card reads:
 * - `writing` — arguments are still streaming; nothing is committed yet.
 * - `ready`   — the write succeeded; the file exists and can be opened.
 *
 * There is no failure state: a rejected write produced no file, so it renders
 * as the generic tool-call card instead (see `renderArtifactFileCard`).
 */
export type TAgentArtifactCardStatus = 'writing' | 'ready';

interface IAgentArtifactFileCardProps {
  file: IAgentArtifactFile;
  status: TAgentArtifactCardStatus;
}

/**
 * A file the agent produced, as a chat card — the ChatGPT / Claude pattern: a
 * compact tile with a type glyph, the filename, and its type and size, that
 * opens the file when clicked.
 *
 * Rendered instead of the generic tool-call card, which showed `artifact_write`
 * as a raw argument dump — a 6KB HTML document inlined as an unreadable blob of
 * text in the transcript. The point of the card is that the file's CONTENT is
 * not in the conversation: the transcript says a file exists, and opening it is
 * a deliberate click.
 */
export function AgentArtifactFileCard({
  file,
  status,
}: IAgentArtifactFileCardProps) {
  const { t } = useTranslation('agent-builder');
  const viewer = useAgentArtifactViewer();

  const isLoading = viewer?.loadingUri === file.uri;
  // Openable only once the bytes are committed, and only where a viewer is
  // mounted (see `useAgentArtifactViewer` — the card can outlive its provider).
  const canOpen = status === 'ready' && viewer !== undefined && !isLoading;

  const typeLabel = getArtifactTypeLabel(file.extension);
  const sizeLabel =
    file.sizeBytes !== undefined ? formatBytes(file.sizeBytes) : '';
  const metaLabel = [typeLabel, sizeLabel].filter(Boolean).join(' · ');

  const subtitle =
    status === 'writing'
      ? t('agentBuilder.test.files.writing')
      : // The agent's own description is the more useful line when it wrote one;
        // type and size stay available as the card's tooltip.
        (file.description ?? metaLabel);

  const handleOpen = () => {
    if (!canOpen) return;
    viewer?.openFile(file);
  };

  return (
    <Card
      // Semantics before styling: a card that opens a file IS a button, so it
      // announces itself as one and answers Enter/Space. A plain div with an
      // onClick is unreachable by keyboard.
      role={canOpen ? 'button' : undefined}
      tabIndex={canOpen ? 0 : undefined}
      aria-disabled={canOpen ? undefined : true}
      aria-label={
        canOpen
          ? t('agentBuilder.test.files.openFile', { filename: file.filename })
          : undefined
      }
      onClick={handleOpen}
      onKeyDown={event => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        // Space scrolls the chat by default, which would yank the transcript
        // out from under the user as the viewer opens.
        event.preventDefault();
        handleOpen();
      }}
      title={metaLabel || file.filename}
      data-testid="agent-artifact-file-card"
      className={cn(
        'group w-full max-w-sm p-3 transition-colors',
        canOpen &&
          'cursor-pointer hover:border-primary/40 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      )}
    >
      <Flex gap={3} align="center" className="min-w-0">
        {/* Fixed-size tile keeps the filename baseline steady whether the card
            is still writing or ready to open. */}
        <Flex
          align="center"
          justify="center"
          className="size-10 shrink-0 rounded-lg bg-muted text-muted-foreground"
        >
          {isLoading || status === 'writing' ? (
            <Spinner size="sm" />
          ) : (
            <Icon type={getArtifactFileIcon(file.extension)} size="md" />
          )}
        </Flex>

        <Flex direction="column" className="min-w-0 flex-1" gap={0.5}>
          <Text level="small" weight="medium" className="truncate">
            {file.filename}
          </Text>
          <Text level="xSmall" color="muted" className="truncate">
            {subtitle}
          </Text>
        </Flex>

        {/* An affordance, not a second action: the whole card is the button, so
            this only has to say "this opens". Absent while writing, where there
            is nothing to open yet. */}
        {canOpen ? (
          <Icon
            type="ArrowUpRight"
            size="sm"
            className="shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
          />
        ) : null}
      </Flex>
    </Card>
  );
}
